/**
 * Gráfica de "Órdenes" por período (presentacional + su hook de datos).
 *
 * Réplica de la gráfica de órdenes del SSR (`advanced_stats_with_charts.html`,
 * `loadOrdersChart`): la serie de cantidad de órdenes por día/semana/mes. Gestiona
 * su propio período (mini-pestañas "Día / Sem. / Mes") y su query (`useOrdersChart`,
 * cacheada por sede + período). El `enabled` se hereda de la pantalla (gating
 * `advanced_stats`); el `ChartCard` contempla carga/error.
 */
import { useState } from "react";

import {
  DEFAULT_ORDERS_PERIOD,
  ORDERS_CHART_PERIOD_OPTIONS,
} from "../constants";
import { formatPeriodLabel, sortByPeriodAsc } from "../format";
import { useOrdersChart } from "../hooks/useStats";
import type { OrdersPeriod } from "../schemas";
import type { OrdersChartStats } from "../types";
import { BarChart } from "./BarChart";
import { ChartCard } from "./ChartCard";
import { PeriodTabs } from "./PeriodTabs";

interface OrdersChartProps {
  enabled: boolean;
}

export function OrdersChart({ enabled }: OrdersChartProps) {
  const [period, setPeriod] = useState<OrdersPeriod>(DEFAULT_ORDERS_PERIOD);
  const query = useOrdersChart(period, enabled);

  return (
    <ChartCard<OrdersChartStats>
      title="Órdenes"
      action={
        <PeriodTabs
          value={period}
          options={ORDERS_CHART_PERIOD_OPTIONS}
          onChange={setPeriod}
          ariaLabel="Período de órdenes"
        />
      }
      isPending={query.isPending}
      isError={query.isError}
      error={query.error}
      data={query.data}
    >
      {(stats) => (
        <BarChart
          data={sortByPeriodAsc(stats.data).map((point) => ({
            label: formatPeriodLabel(point.period, period),
            value: point.count,
          }))}
        />
      )}
    </ChartCard>
  );
}
